import { useServices } from "@hq/client-react";
import { Button, Dialog, Input, Kbd, toast } from "@z/ui";
import { Inbox } from "lucide-react";
import { useRef, useState } from "react";

/** Capture literal text in Inbox without inheriting the active project, note, or date. */
export function QuickCapture(props: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const services = useServices();
  const inputRef = useRef<HTMLInputElement>(null);
  const inFlight = useRef(false);
  const [title, setTitle] = useState("");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState(false);

  const capture = async () => {
    if (!title.trim() || inFlight.current) return;
    inFlight.current = true;
    setPending(true);
    setError(false);
    try {
      await services.tasks.create({ title: title.trim() });
      setTitle("");
      props.onOpenChange(false);
      toast({ title: "Added to Inbox", variant: "success" });
    } catch {
      setError(true);
    } finally {
      inFlight.current = false;
      setPending(false);
    }
  };

  return (
    <Dialog.Root
      open={props.open}
      onOpenChange={(open) => {
        if (!inFlight.current) props.onOpenChange(open);
      }}
    >
      <Dialog.Content
        initialFocus={inputRef}
        showClose={!pending}
        className="w-[calc(100%-2rem)]"
        aria-busy={pending}
      >
        <Dialog.Header>
          <Dialog.Title className="flex items-center gap-2">
            <Inbox aria-hidden="true" className="size-5 text-accent" />
            Add to Inbox
          </Dialog.Title>
          <Dialog.Description>Capture now, organize later.</Dialog.Description>
        </Dialog.Header>
        <form
          className="flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            void capture();
          }}
        >
          <Input
            ref={inputRef}
            aria-label="To-do title"
            placeholder="What do you need to do?"
            autoComplete="off"
            value={title}
            readOnly={pending}
            className="h-11"
            onChange={(event) => {
              setTitle(event.target.value);
              setError(false);
            }}
            onKeyDown={(event) => {
              if (event.key !== "Enter" || event.nativeEvent.isComposing || event.keyCode === 229)
                return;
              event.preventDefault();
              void capture();
            }}
          />
          {error && (
            <p role="alert" className="text-sm text-danger">
              Could not add this to-do. Your draft is still here; try again.
            </p>
          )}
          <Dialog.Footer>
            <Button
              variant="secondary"
              disabled={pending}
              onClick={() => props.onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={pending || !title.trim()}>
              {pending ? "Adding…" : "Add to Inbox"}
              <Kbd aria-hidden="true">↵</Kbd>
            </Button>
          </Dialog.Footer>
        </form>
      </Dialog.Content>
    </Dialog.Root>
  );
}
